import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react"
import trailMessage from "@rogue_4/pages/Game/data/json/trail.json"
import anime from "animejs"
import classNames from "classnames"
import { noop } from "lodash"

import { StyledComponent } from "@/topic/rogue_4/types"
import ANIMATION from "../../animation"
import GAME_EMITTER from "../../components/Emitter"
import { useGameEffectModal } from "../../components/Modal/store"
import Toast from "../../components/Toast"
import { useDivRef } from "../../hooks/useDivRef"
import { useEvent } from "../../hooks/useEvent"
import { IAnimateRef } from "../../type"
import { SFX } from "../../utils/sound"
import { useNextScene } from "../store"

import styles from "./index.module.less"

export const TypeToast: StyledComponent<{
    text: string
    onTypeEnd: () => void
    delay?: number
}> = ({ text, onTypeEnd, delay = 75, className, style }) => {
    const [indexState, setIndex] = useState(0)
    const [textR, setTextR] = useState("")
    const cbRef = useRef<() => void>(noop)
    useEffect(() => {
        setIndex(0)
        setTextR(text)
        let index = 0
        const cb = () => {
            if (index < text.length - 1) {
                index = text.length - 1
                GAME_EMITTER.off("NEXT", cb)
            }
        }
        GAME_EMITTER.on("NEXT", cb)
        const timer = setInterval(() => {
            if (index >= text.length) {
                window.clearInterval(timer)
                GAME_EMITTER.off("NEXT", cb)
                cbRef.current?.()
                return
            }
            setIndex(++index)
        }, delay)
        return () => {
            window.clearInterval(timer)
            GAME_EMITTER.off("NEXT", cb)
        }
    }, [text])
    useEffect(() => {
        cbRef.current = onTypeEnd ?? noop
    }, [onTypeEnd])
    return (
        <Toast
            className={classNames(styles.mainToast, className)}
            style={style}
            active={!!text}
        >
            {textR.split("").map((s, index) => (
                <span
                    className={classNames(
                        styles.char,
                        index < indexState && styles.active
                    )}
                    key={`${index}-${s}`}
                >
                    {s}
                </span>
            ))}
        </Toast>
    )
}

const TrailScene = React.forwardRef<IAnimateRef>((_, ref) => {
    const nextScene = useNextScene()
    const bgRef = useDivRef()
    const doorRef = useDivRef()
    const decoRef = useDivRef()

    const openRef = useRef(false)
    const callEffectModal = useGameEffectModal()
    const openDoor = useCallback(() => {
        SFX.play("OpenDoor")
        if (openRef.current) return
        openRef.current = true
        const left = doorRef.current?.querySelector(
            `.${styles.doorLeft}`
        ) as HTMLDivElement
        const right = doorRef.current?.querySelector(
            `.${styles.doorRight}`
        ) as HTMLDivElement
        const clickCircle = decoRef.current?.querySelector(
            `.${styles.clickCircle}`
        ) as HTMLDivElement
        const enterCircle = decoRef.current?.querySelector(
            `.${styles.enterCircle}`
        ) as HTMLDivElement
        const enterLight = decoRef.current?.querySelector(
            `.${styles.enterLight}`
        ) as HTMLDivElement
        // const easing = () => (time) => {
        //     if (time < 0.01) {
        //         return time * 10
        //     } else if (time < 0.2) return 0.1
        //     else return 1.4 * (time - 0.2) * (time - 0.2) + 0.1
        // }
        const duration = 2000
        anime
            .timeline()
            .add(
                {
                    targets: left,
                    translateX: "-50%",
                    translateZ: [0, 0],
                    duration,
                    easing: "easeInCubic",
                },
                0
            )
            .add(
                {
                    targets: right,
                    translateX: "50%",
                    translateZ: [0, 0],
                    duration,
                    easing: "easeInCubic",
                },
                0
            )
            .add(
                {
                    targets: [clickCircle, enterCircle, enterLight],
                    opacity: 0,
                    duration: 300,
                    easing: "easeInCubic",
                },
                0
            )
            .add(
                {
                    begin: () => {
                        callEffectModal("whiteMask")
                    },
                },
                0
            )
            .finished.then(() => {
                nextScene()
            })
    }, [callEffectModal, decoRef, doorRef, nextScene])

    // 线性流程
    // 0 init 1~trail.length massage
    const [step, setStep] = useState(0)
    const lockRef = useRef(false)
    const nextStep = useEvent(() => {
        // openDoor()
        // return
        // FIXME remove this
        // nextScene()
        // return
        if (lockRef.current) {
            return
        }
        setStep(step + 1)
        if (step <= trailMessage.length) {
            lockRef.current = true
        }
        if (step === trailMessage.length + 2) {
            nextScene()
        }
    })
    const onTypeEnd = useCallback(() => {
        if (step >= 1 && step <= trailMessage.length) {
            lockRef.current = false
        }
    }, [step])
    const text = useMemo(
        () =>
            step >= 1 && step <= trailMessage.length
                ? trailMessage[step - 1]
                : "",
        [step]
    )
    useEffect(() => {
        if (step > trailMessage.length) return
        const cb = () => {
            nextStep()
        }
        GAME_EMITTER.on("NEXT", cb)
        return () => {
            GAME_EMITTER.off("NEXT", cb)
        }
    }, [nextStep, step])

    const onClick = useCallback(() => {
        if (step === trailMessage.length + 1) openDoor()
    }, [step, openDoor])

    useImperativeHandle(
        ref,
        () => ({
            fadeIn: async () => {
                const inst = anime.timeline()
                inst.add(
                    ANIMATION.in(
                        doorRef,
                        {},
                        { duration: 1500, scale: [0.6, 1] }
                    ),
                    0
                )
                inst.add(ANIMATION.in(bgRef, {}, { duration: 1000 }), 1200)
                inst.finished.then(() => {
                    nextStep()
                })
                return inst.finished
            },
            fadeOut: async () => undefined,
        }),
        [bgRef, doorRef, nextStep]
    )

    return (
        <div className={styles.trailScene}>
            <div className={styles.bgContainer} ref={bgRef}>
                <div
                    className={styles.bg}
                    style={{
                        opacity: Math.min(
                            1,
                            ((step / (trailMessage.length + 1)) * step) /
                                (trailMessage.length + 1)
                        ).toFixed(2),
                    }}
                ></div>
            </div>

            <div className={classNames(styles.door)} ref={doorRef}>
                <div className={styles.contentContainer}>
                    <div className={styles.doorLeft}></div>
                    <div className={styles.doorRight}></div>
                    <div className={styles.doorBorder}></div>
                </div>
            </div>
            <div
                className={classNames(
                    styles.enterDeco,
                    step > trailMessage.length && styles.active
                )}
                ref={decoRef}
            >
                <div className={styles.enterLight}></div>
                <div className={styles.enterCircle}></div>
                <div className={styles.clickCircle} onClick={onClick}></div>
            </div>
            <TypeToast onTypeEnd={onTypeEnd} text={text} />
        </div>
    )
})
TrailScene.displayName = "TrailScene"

export default TrailScene
